import { memo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Link2, AlertTriangle, Lock } from 'lucide-react'
import type { JiraIssue } from '@/types/jira'
import JiraTaskCard from './JiraTaskCard'

interface JiraDependenciesProps {
  blockedByIssues: JiraIssue[]
  blockingIssues: JiraIssue[]
}

const JiraDependencies = memo(function JiraDependencies({ 
  blockedByIssues,
  blockingIssues
}: JiraDependenciesProps) {
  const hasDependencies = blockedByIssues.length > 0 || blockingIssues.length > 0

  return (
    <Card variant="default" padding="md">
      <CardHeader compact>
        <CardTitle compact className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-primary-600" />
          Dependencies
          {hasDependencies && (
            <span className="text-xs font-normal text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 rounded-full">
              {blockedByIssues.length + blockingIssues.length} linked
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent compact>
        {!hasDependencies ? (
          <div className="text-center py-8">
            <Link2 className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No blocking dependencies found
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              This section will show linked issues when available
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {blockedByIssues.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Lock className="h-4 w-4 text-red-500" />
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Blocked By ({blockedByIssues.length})
                  </h4>
                </div>
                <div className="space-y-2">
                  {blockedByIssues.map(issue => (
                    <JiraTaskCard key={issue.id} task={issue} compact />
                  ))}
                </div>
              </div>
            )}

            {blockingIssues.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    Blocking Others ({blockingIssues.length})
                  </h4>
                </div>
                <div className="space-y-2">
                  {blockingIssues.map(issue => (
                    <JiraTaskCard key={issue.id} task={issue} compact />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
})

export default JiraDependencies

